import {
  Component,
  forwardRef,
  ViewChild,
  ElementRef,
  OnInit,
  Input,
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  FormBuilder,
  ControlValueAccessor,
  FormGroup,
} from "@angular/forms";
import { Observable } from "rxjs";
import { switchMap, filter, debounceTime, tap, finalize } from "rxjs/operators";
import { PhysicianService } from "../../../services/physician.service";
import { ReferringPhysicianFilter } from "src/app/model/referring-physician";
import { ReferringPhysicianDialogComponent } from "../../dialogs/referring-physician-edit/referring-physician-edit-dialog.component";
import { MatDialog } from "@angular/material";
import { ValidateAutoCompleteSelect } from "../../../validators/autocomplete-select.validator";

export const REFERRING_PHYSICIAN_AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ReferringPhysicianCompleteComponent),
  multi: true,
};

@Component({
  // tslint:disable-next-line:component-selector
  selector: "referring-physician-autocomplete",
  providers: [REFERRING_PHYSICIAN_AUTOCOMPLETE_VALUE_ACCESSOR],
  templateUrl: "./referring-physician-autocomplete.component.html",
  styleUrls: ["./referring-physician-autocomplete.component.css"],
})
export class ReferringPhysicianCompleteComponent
  implements OnInit, ControlValueAccessor
{
  onChange;
  filteredReferringPhysicianFilter: Observable<ReferringPhysicianFilter[]>;
  form: FormGroup;
  referringPhysiciansFilter: ReferringPhysicianFilter[] = [];
  selecting: boolean;
  loading: boolean;
  @Input() canEdit: boolean = true;
  @Input() readOnly: boolean;
  @ViewChild("referringPhysicianCtrl", { static: false })
  referringPhysicianCtrl: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private physicianService: PhysicianService,
    private dialog: MatDialog
  ) {
    this.form = this.formBuilder.group({
      ReferringPhysicianCtrl: [null],
    });
  }

  get f() {
    return this.form.controls;
  }

  get isSelected() {
    return (
      !this.form.controls.ReferringPhysicianCtrl.value ||
      typeof this.form.controls.ReferringPhysicianCtrl.value !== "string"
    );
  }

  displayFn(item?: ReferringPhysicianFilter): string | undefined {
    return item ? item.Name + " [" + item.Enrollment + "]" : undefined;
  }

  edit(): void {
    if (
      !ValidateAutoCompleteSelect(this.form.controls.ReferringPhysicianCtrl)
    ) {
      const dialogRef = this.dialog.open(ReferringPhysicianDialogComponent, {
        width: "90%",
        data: this.form.controls.ReferringPhysicianCtrl.value.Id,
      });

      dialogRef
        .afterClosed()
        .subscribe(
          async (referringPhysicianFilter: ReferringPhysicianFilter) => {
            if (referringPhysicianFilter) {
              this.referringPhysiciansFilter = [];
              this.referringPhysicianFilterSelected(referringPhysicianFilter);
            }
          }
        );
    }
  }

  create(): void {
    const dialogRef = this.dialog.open(ReferringPhysicianDialogComponent, {
      width: "90%",
    });

    dialogRef
      .afterClosed()
      .subscribe(async (referringPhysicianFilter: ReferringPhysicianFilter) => {
        if (referringPhysicianFilter) {
          this.referringPhysiciansFilter = [];
          this.referringPhysicianFilterSelected(referringPhysicianFilter);
        }
      });
  }

  ngOnInit() {
    this.f.ReferringPhysicianCtrl.valueChanges
      .pipe(
        filter((value) => {
          if (value != null && value !== "" && typeof value === "string") {
            return true;
          } else {
            if (value == null || value === "") {
              this.referringPhysiciansFilter = [];
            }
            return false;
          }
        }),
        debounceTime(400),
        tap(() => (this.loading = true)),
        switchMap((value) =>
          this.physicianService
            .getReferringPhysiciansFilter(value)
            .pipe(finalize(() => (this.loading = false)))
        )
      )
      .subscribe((values) => (this.referringPhysiciansFilter = values));
  }

  referringPhysicianFilterSelected(
    referringPhysicianFilter: ReferringPhysicianFilter
  ) {
    this.selecting = true;
    this.form.patchValue({ ReferringPhysicianCtrl: referringPhysicianFilter });
    this.onChange(referringPhysicianFilter);
  }

  onReferringPhysicianFilterChange() {
    this.onChange(null);
  }

  writeValue(obj: any): void {
    this.form.patchValue({ ReferringPhysicianCtrl: obj });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  setFocus(): void {
    this.referringPhysicianCtrl.nativeElement.focus();
  }
}
